import { differenceInDays } from "date-fns";
import { getDateFromValue } from "src/utils/time";
import { LOCALE, URL_DATE_DELIMITERS } from "./TimeWindow.constants";

/**
 * Used for a single dtate/date-string
 */
export const urlTimeWindow = {
  /**
   * serialize a date to a string
   * Example: Date(2025, 6, 15, 14) -> "7-15-2025_14"
   */
  serializeDate: (date: Date | undefined, withHours: boolean = false): string => {
    if (!date) return "";

    if (withHours) {
      // Use toLocaleString() to get both date and time, then format for URL
      return (
        date
          .toLocaleString(LOCALE, {
            hour12: false,
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "numeric",
          })
          // .replaceAll("/", urlTimeWindow.DATE_DELIMITER) // 7/15/2025 -> 7-15-2025
          .replace(", ", URL_DATE_DELIMITERS.TIME_DELIMITER)
      ); // "2025-7-15, 14" -> "2025-7-15_14"
    }

    return date.toLocaleDateString(LOCALE);
    // .replaceAll("/", urlTimeWindow.DATE_DELIMITER);
  },

  /**
   * deserialize a (single) date string to a Date object
   * Example: "7-15-2025_14" -> Date(2025, 6, 15, 14)
   */
  deserializeDateStr: (str: string): Date => {
    const parts = str.split(URL_DATE_DELIMITERS.TIME_DELIMITER);
    const datePart = parts[0]; // "7-15-2025"
    const hourPart = parts[1]; // "14" or undefined

    const [year, month, day] = datePart.split("-").map(Number);
    const hour = hourPart ? Number(hourPart) : 0;

    // month is 0-based in Date constructor
    return new Date(year, month - 1, day, hour);
  },

  /**
   * serialize a date range to a string
   * When the days diff is above 1: "1-15-2025__1-18-2025"
   * When the days diff is less than 1, add hours: "1-15-2025_14__1-15-2025_15"
   */
  serializeDateRange: (dateRange: [Date, Date]): string => {
    const [start, end] = dateRange;
    const startDate = getDateFromValue(start);
    const endDate = getDateFromValue(end);

    if (!("getTime" in startDate) || !("getTime" in endDate)) {
      console.warn("Invalid date range ", dateRange);
      return "";
    }

    const daysDiff = differenceInDays(endDate, startDate);
    const withHours = daysDiff < 1;
    const startStr = urlTimeWindow.serializeDate(startDate, withHours);
    const endStr = urlTimeWindow.serializeDate(endDate, withHours);

    return `${startStr}${URL_DATE_DELIMITERS.RANGE_DELIMITER}${endStr}`;
  },

  /**
   * deserialize a date range string to a tuple of Date objects
   */
  deserializeDateRangeStr: (
    str: string | number | undefined
  ): [Date, Date] | undefined => {
    if (!str) return;

    const [startStr, endStr] = str
      .toString()
      .split(URL_DATE_DELIMITERS.RANGE_DELIMITER);

    const isValidDateStr = (str: string): boolean => {
      if (!str) return false;

      const date = urlTimeWindow.deserializeDateStr(str);
      return "getTime" in date;
    };

    if (!isValidDateStr(startStr) || !isValidDateStr(endStr)) {
      return;
    }

    const startDate = urlTimeWindow.deserializeDateStr(startStr);
    const endDate = urlTimeWindow.deserializeDateStr(endStr);

    return [startDate, endDate];
  },
};
